import i18n from '../i18n'

let stripeInstance = null

export async function initStripe(publishableKey, stripeAccountId = null) {
  // Wait for Stripe to be loaded from CDN
  if (typeof window.Stripe === 'undefined') {
    await new Promise((resolve, reject) => {
      let attempts = 0
      const maxAttempts = 50 // 5 seconds max wait
      const checkStripe = setInterval(() => {
        attempts++
        if (typeof window.Stripe !== 'undefined') {
          clearInterval(checkStripe)
          resolve()
        } else if (attempts >= maxAttempts) {
          clearInterval(checkStripe)
          reject(new Error('Stripe.js failed to load from CDN'))
        }
      }, 100)
    })
  }

  // Publishable key must be provided by backend
  if (!publishableKey) {
    throw new Error('Stripe publishable key is required. Please ensure the backend provides publishableKey in the payment data.')
  }

  // Initialize Stripe with stripeAccount for Direct Charges (Connected Accounts)
  const stripeOptions = stripeAccountId ? { stripeAccount: stripeAccountId } : {}
  stripeInstance = window.Stripe(publishableKey, stripeOptions)
  
  return stripeInstance
}

export async function createStripePaymentElement(
  stripe,
  clientSecret,
  elementSelector,
  readyCallback,
  beforeSubmitCallback,
  paymentCompletedCallback,
  errorCallback
) {
  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#54c100',
      colorDanger: '#dc3545',
      fontSizeBase: '1rem',
      fontLineHeight: '1rem',
      borderRadius: '4px',
    },
    rules: {
      '.Input': {
        padding: '20px 15px',
        boxShadow: 'none',
        borderWidth: '1px',
      },
      '.Input--invalid': {
        boxShadow: 'none',
      },
      '.Input:focus': {
        boxShadow: 'none',
      },
    }
  }

  const elements = stripe.elements({
    clientSecret,
    appearance,
    locale: i18n.locale
  })

  const paymentElement = elements.create('payment', {
    layout: 'accordion', // tabs or auto
    //  wallets: {
    //   link: 'never'
    // },
  })

  paymentElement.on('ready', () => {
    if (readyCallback) {
      readyCallback()
    }
  })

  paymentElement.on('change', (event) => {
    if (event.error && errorCallback) {
      errorCallback(event.error)
    }
  })

  paymentElement.on('loaderror', (event) => {
    console.error('Payment Element load error:', event)
    if (errorCallback) {
      errorCallback(new Error('Failed to load payment form'))
    }
  })

  await paymentElement.mount(elementSelector)

  return {
    element: paymentElement,
    elements,
    stripe,
    submit: async () => {
      try {
        if (beforeSubmitCallback) {
          const shouldProceed = await beforeSubmitCallback()
          if (shouldProceed === false) {
            return { error: { message: 'Validation failed' } }
          }
        }

        const { error: submitError } = await elements.submit()
        if (submitError) {
          if (errorCallback) {
            errorCallback(submitError)
          }
          return { error: submitError }
        }

        const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.origin + window.location.pathname,
          },
          redirect: 'if_required',
        })

        if (confirmError) {
          if (errorCallback) {
            errorCallback(confirmError)
          }
          return { error: confirmError }
        }

        if (paymentIntent) {
          // Handle different payment statuses
          if (paymentIntent.status === 'succeeded') {
            if (paymentCompletedCallback) {
              paymentCompletedCallback({
                success: true,
                paymentIntent,
              })
            }
            return { success: true, paymentIntent }
          } else if (paymentIntent.status === 'processing') {
            // Payment is processing, still consider it a success
            if (paymentCompletedCallback) {
              paymentCompletedCallback({
                success: true,
                paymentIntent,
              })
            }
            return { success: true, paymentIntent }
          } else if (paymentIntent.status === 'requires_action' || paymentIntent.status === 'requires_payment_method') {
            // Payment needs additional action (3D Secure, etc.)
            // Stripe will handle redirect automatically with redirect: 'if_required'
            return { success: false, paymentIntent, requiresAction: true }
          } else {
            // Other statuses (requires_capture, canceled, etc.)
            return { 
              success: false, 
              paymentIntent,
              error: { message: `Payment status: ${paymentIntent.status}` }
            }
          }
        }

        return { success: false, error: { message: 'Payment intent not returned' } }
      } catch (error) {
        if (errorCallback) {
          errorCallback(error)
        }
        return { error }
      }
    },
    unmount: () => {
      paymentElement.unmount()
    },
    update: () => {
      // Stripe elements automatically update, but we can refresh if needed
      paymentElement.unmount()
      paymentElement.mount(elementSelector)
    },
  }
}
